import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link as RouterLink } from 'react-router-dom';
import { Person as PersonIcon, StraightenOutlined } from '@mui/icons-material';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { usePersonLabels } from '@/hooks/usePersonLabels';
// Use shared service-layer type to avoid divergence
import type { BilletData, IEMCustomer } from '@/services/iem/types';

type ProfileSummaryProps = {
  person?: IEMCustomer;
  loading?: boolean;
  onManageSizing?: () => void;
  onClearIndividual?: () => Promise<void> | void;
  onReactivateIndividual?: () => Promise<void> | void;
  stateActionPending?: boolean;
};

export const ProfileSummary: React.FC<ProfileSummaryProps> = ({
  person,
  loading,
  onManageSizing,
  onClearIndividual,
  onReactivateIndividual,
  stateActionPending,
}) => {
  const [confirmAction, setConfirmAction] = useState<'clear' | 'reactivate' | null>(null);
  const isViewer = useWriteGuard();
  const personLabels = usePersonLabels('front_window');

  if (loading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </Stack>
    );
  }

  if (!person) {
    return (
      <Typography variant="body2" color="text.secondary">
        No customer selected.
      </Typography>
    );
  }

  const billet = (person.billet ?? null) as BilletData | null;
  const billetPositionTitle = billet?.positionTitle?.trim();
  const billetDutyLocation = billet?.dutyLocation?.trim();
  const billetPrimary = billetPositionTitle || billetDutyLocation || '—';
  const billetSecondary = (() => {
    if (!billet) return undefined;
    if (billetDutyLocation && billetDutyLocation !== billetPrimary) return billetDutyLocation;
    if (billet.supervisor) {
      const supervisor = billet.supervisor.trim();
      if (supervisor.length > 0) return `Supervisor: ${supervisor}`;
    }
    return undefined;
  })();

  const positionDisplay = person.position?.trim() || '—';
  const phrCount = person.iem_status?.phr_items_count ?? 0;
  const isCleared = person.cleared_status === true;
  const canClear = phrCount === 0;
  const actionDisabled =
    Boolean(stateActionPending) || (!isCleared && (!canClear || !onClearIndividual));
  const actionLabel = isCleared ? 'Reactivate' : 'Clear';
  const actionTooltip = isCleared
    ? `Reactivate ${personLabels.lower} for transactions`
    : canClear
      ? `Mark ${personLabels.lower} as cleared`
      : `${personLabels.label} has ${phrCount} item(s) on hand receipt. All items must be returned before clearing.`;
  const clearedAtLabel = (() => {
    if (!person.cleared_at) return null;
    const parsed = new Date(person.cleared_at);
    if (Number.isNaN(parsed.getTime())) return person.cleared_at;
    return parsed.toLocaleString();
  })();
  const displayName = person.display_name?.trim() || 'Unknown Person';

  const handleConfirm = async () => {
    const action = confirmAction;
    setConfirmAction(null);

    if (action === 'clear') {
      await Promise.resolve(onClearIndividual?.());
      return;
    }

    if (action === 'reactivate') {
      await Promise.resolve(onReactivateIndividual?.());
    }
  };

  const photoInitials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

  return (
    <Box>
      {/* Profile photo option — renders when photoUrl is available (#339) */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
        <Avatar
          src={person.photoUrl}
          alt={`Profile photo of ${displayName}`}
          aria-label={`Profile photo of ${displayName}`}
          sx={{ width: 48, height: 48, flexShrink: 0, bgcolor: 'primary.light' }}
        >
          {photoInitials || <PersonIcon fontSize="small" />}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            component={RouterLink}
            to={`/person/${person.id}`}
            sx={{
              fontWeight: 700,
              lineHeight: 1.1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {displayName}
          </Typography>
        </Box>
      </Stack>

      {onManageSizing && !isViewer && (
        <Button
          variant="outlined"
          size="small"
          fullWidth
          startIcon={<StraightenOutlined />}
          onClick={onManageSizing}
          sx={{ mt: 1 }}
        >
          Sizing
        </Button>
      )}

      {(onClearIndividual || onReactivateIndividual) && !isViewer && (
        <Tooltip title={actionTooltip}>
          <span>
            <Button
              variant="contained"
              size="small"
              fullWidth
              color={isCleared ? 'warning' : 'error'}
              disabled={actionDisabled}
              onClick={() => setConfirmAction(isCleared ? 'reactivate' : 'clear')}
              sx={{ mt: 1 }}
            >
              {stateActionPending ? 'Working…' : actionLabel}
            </Button>
          </span>
        </Tooltip>
      )}

      {isCleared && (
        <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 1 }}>
          Cleared{clearedAtLabel ? ` on ${clearedAtLabel}` : ''}.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ letterSpacing: 1, fontWeight: 700 }}
          >
            SECTION:
          </Typography>
          <Typography variant="body1">{person.unit || '—'}</Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ letterSpacing: 1, fontWeight: 700 }}
          >
            MOS:
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {person.mos || '—'}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ letterSpacing: 1, fontWeight: 700 }}
          >
            WORKFORCE CATEGORY:
          </Typography>
          <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>
            {billetPrimary}
          </Typography>
          {billetSecondary && (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {billetSecondary}
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ letterSpacing: 1, fontWeight: 700 }}
          >
            POSITION:
          </Typography>
          <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>
            {positionDisplay}
          </Typography>
        </Grid>
      </Grid>

      <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
        <DialogTitle>
          {confirmAction === 'clear'
            ? `Confirm Clear ${personLabels.label}`
            : `Confirm Reactivate ${personLabels.label}`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmAction === 'clear'
              ? `This will mark the ${personLabels.lower} as cleared and disable Front Window workflows.`
              : `This will reactivate the ${personLabels.lower} and re-enable Front Window workflows.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction === 'clear' ? 'error' : 'warning'}
            onClick={() => {
              void handleConfirm();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
